import { ComponentProps, ReactNode } from 'react';

import classNames from 'classnames';

import { Flex, Text } from '@spotify-internal/encore-web';
import { Box } from '@spotify-internal/encore-web/next';

import { useMediaQuery } from '../../hooks';
import { ConfidenceLogo } from '../Icons';
import { ChartBackground } from './ChartBackground';
import { Footer } from './Footer';

import styles from './AuthScreen.module.css';

type AuthScreenRootProps = ComponentProps<'div'> & {
  children: ReactNode;
};

const Root = ({ children, className, ...props }: AuthScreenRootProps) => (
  <ChartBackground>
    <div {...props} className={classNames(styles.root, className)}>
      {children}
      <Footer />
    </div>
  </ChartBackground>
);

const Main = ({
  children,
  className,
  ...props
}: ComponentProps<'div'>): ReactNode => (
  <div {...props} className={classNames(styles.main, className)}>
    {children}
  </div>
);

const MOBILE_MEDIA_QUERY = '(width <= 767px)';

const FormContainer = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode => {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  return isMobile ? (
    <Box
      variant="elevated"
      borderRadius="larger-2"
      padding="base"
      className={className}
    >
      {children}
    </Box>
  ) : (
    <div className={className}>{children}</div>
  );
};

const Logo = ({
  className,
  ...props
}: {
  className?: string;
  color?: string;
}): ReactNode => (
  <ConfidenceLogo {...props} className={classNames(styles.logo, className)} />
);

const Title = ({
  className,
  children,
  ...props
}: ComponentProps<typeof Text>): ReactNode => (
  <Text
    as="h1"
    variant="titleLarge"
    {...props}
    className={classNames(styles.title, className)}
  >
    {children}
  </Text>
);

const Description = ({
  className,
  children,
  ...props
}: ComponentProps<typeof Text>): ReactNode => (
  <Text
    as="div"
    variant="bodyMedium"
    semanticColor="textSubdued"
    {...props}
    className={classNames(styles.description, className)}
  >
    {children}
  </Text>
);

const Actions = ({
  className,
  children,
  ...props
}: ComponentProps<typeof Flex>): ReactNode => (
  <Flex
    gap="base"
    justifyContent="center"
    {...props}
    className={classNames(styles.actions, className)}
  >
    {children}
  </Flex>
);

export const AuthScreen = {
  Root,
  Main,
  FormContainer,
  Logo,
  Title,
  Description,
  Actions,
};
